import React, { useState, PropsWithChildren, useCallback, ContextType } from 'react';
import { debounce, DebouncedFunc } from 'lodash';
import type { DrawerType, DrawerName } from '../types';

export function useDrawerRender(): ContextType<typeof DrawerRenderContext> {
    return React.useContext(DrawerRenderContext);
}

export function useDrawerControls(): DrawerControls {
    const context = React.useContext(DrawerWriteContext);

    if (!context) {
        throw new Error('Missing DrawerWriteContext');
    }

    return context;
}

type DrawerControls = {
    open: DebouncedFunc<(drawer: DrawerType<DrawerName>) => void>;
    close: () => void;
};

export const DrawerWriteContext = React.createContext<undefined | DrawerControls>(undefined);

export const DrawerRenderContext = React.createContext<
    undefined | { drawer: DrawerType<DrawerName>; key: number }
>(undefined);

export function DrawerContextProvider(
    props: PropsWithChildren<{ initialState?: DrawerType<DrawerName> }>
): JSX.Element {
    const [currentRendererState, setCurrentRendererState] = useState<{
        currentKey: number;
        currentDrawer: undefined | DrawerType<DrawerName>;
    }>({ currentKey: 0, currentDrawer: props.initialState });

    const open = useCallback(
        (drawer: DrawerType<DrawerName>) => {
            setCurrentRendererState((prev) => ({
                currentKey: prev.currentKey + 1,
                currentDrawer: drawer,
            }));
        },
        [setCurrentRendererState]
    );

    const close = useCallback(() => {
        setCurrentRendererState((prev) => ({ ...prev, currentDrawer: undefined }));
    }, [setCurrentRendererState]);

    const memoizedControls = React.useMemo(() => {
        return {
            open: debounce(open, 500, { leading: true }),
            close,
        };
    }, [open, close]);

    const memoizedRenderValue = React.useMemo(() => {
        if (currentRendererState.currentDrawer) {
            return {
                drawer: currentRendererState.currentDrawer,
                key: currentRendererState.currentKey,
            };
        }
        return undefined;
    }, [currentRendererState]);

    return (
        <DrawerWriteContext.Provider value={memoizedControls}>
            <DrawerRenderContext.Provider value={memoizedRenderValue}>
                {props.children}
            </DrawerRenderContext.Provider>
        </DrawerWriteContext.Provider>
    );
}
